import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../api/client';
import { Modal, Button, Spinner, Badge, Icon, Toggle, useToast } from '../../components/ds';
import './FieldConfigModal.css';

const CATEGORIES = [
  { value: 'Content', label: 'Content — 文案內容' },
  { value: 'Medias', label: 'Medias — 素材媒體' },
  { value: 'URLs', label: 'URLs — 連結設定' },
  { value: 'Style', label: 'Style — 樣式主題' },
  { value: 'Trackings', label: 'Trackings — 追蹤像素' },
  { value: 'Advance', label: 'Advance — 進階設定' },
];

const TYPE_OVERRIDES = [
  { value: '', label: '（原始）' },
  { value: 'url', label: 'url — 連結輸入' },
  { value: 'color', label: 'color — 色票選擇器' },
  { value: 'textarea', label: 'textarea — 多行文字' },
];

// Build variant from legacy field_config (no "variants" key present).
function synthesizeVariantFromFields(fields) {
  const defaults = {};
  const locked_fields = [];
  for (const f of fields) {
    if (!f.visible && f.hidden_value) {
      const compositeKey = `${f.component_key}:${f.property_key}`;
      defaults[compositeKey] = f.hidden_value;
      locked_fields.push(compositeKey);
    }
  }
  return {
    name: '預設',
    description: '',
    is_default: true,
    defaults,
    locked_fields,
    variant_key: crypto.randomUUID(),
  };
}

function newEmptyVariant(name = '') {
  return {
    name,
    description: '',
    is_default: false,
    defaults: {},
    locked_fields: [],
    variant_key: crypto.randomUUID(),
  };
}

function defaultValueStr(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

// ── Main Modal ─────────────────────────────────────────────────────────────
export default function FieldConfigModal({ template, onClose }) {
  const [fields, setFields] = useState([]);
  const [variants, setVariants] = useState([]);
  const [activeVariantIdx, setActiveVariantIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);
  const [newVariantModal, setNewVariantModal] = useState(null); // { sourceIdx: number|null }
  const initialStateRef = useRef(null);
  const addDropdownRef = useRef(null);
  const { addToast } = useToast();

  const fetchConfig = useCallback(async () => {
    try {
      const data = await api.getTemplateFieldConfig(template.uuid);
      const f = data.fields || [];
      setFields(f);

      let v;
      if (Array.isArray(data.variants) && data.variants.length > 0) {
        v = data.variants;
      } else {
        v = [synthesizeVariantFromFields(f)];
      }
      setVariants(v);
      initialStateRef.current = JSON.stringify({ fields: f, variants: v });
    } catch (err) {
      addToast('載入欄位設定失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  }, [template.uuid, addToast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Close add dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target)) {
        setAddDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function markDirty(nextFields, nextVariants) {
    setDirty(
      JSON.stringify({ fields: nextFields, variants: nextVariants }) !==
        initialStateRef.current
    );
  }

  // ── Grouped fields ─────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const map = {};
    for (const f of fields) {
      const key = `${f.component_key}:${f.component_name}`;
      if (!map[key])
        map[key] = {
          componentKey: f.component_key,
          componentName: f.component_name,
          fields: [],
        };
      map[key].fields.push(f);
    }
    return Object.values(map);
  }, [fields]);

  function getFieldIndex(componentKey, propertyKey) {
    return fields.findIndex(
      (f) => f.component_key === componentKey && f.property_key === propertyKey
    );
  }

  function updateField(idx, patch) {
    setFields((prev) => {
      const next = prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      markDirty(next, variants);
      return next;
    });
  }

  // ── Variant helpers ───────────────────────────────────────────────────
  function updateVariant(idx, patch) {
    setVariants((prev) => {
      const next = prev.map((v, i) => (i === idx ? { ...v, ...patch } : v));
      markDirty(fields, next);
      return next;
    });
  }

  function duplicateVariant(sourceIdx) {
    setNewVariantModal({ sourceIdx });
    setAddDropdownOpen(false);
  }

  function addBlankVariant() {
    setNewVariantModal({ sourceIdx: null });
    setAddDropdownOpen(false);
  }

  function confirmNewVariant(name) {
    const { sourceIdx } = newVariantModal;
    setNewVariantModal(null);
    setVariants((prev) => {
      let newV;
      if (sourceIdx !== null) {
        const src = prev[sourceIdx];
        newV = {
          name,
          description: src.description || '',
          is_default: false,
          defaults: { ...src.defaults },
          locked_fields: [...(src.locked_fields || [])],
          variant_key: crypto.randomUUID(),
        };
      } else {
        newV = newEmptyVariant(name);
      }
      const next = [...prev, newV];
      setActiveVariantIdx(next.length - 1);
      markDirty(fields, next);
      return next;
    });
  }

  function deleteVariant(idx) {
    if (variants[idx].is_default) return;
    if (!window.confirm(`確定刪除 Variant「${variants[idx].name}」？`)) return;
    setVariants((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      setActiveVariantIdx((cur) => Math.min(cur, next.length - 1));
      markDirty(fields, next);
      return next;
    });
  }

  // Per-variant default value update (override / inherit)
  // key must be a composite key: `${component_key}:${property_key}`
  function setVariantDefault(variantIdx, key, value) {
    setVariants((prev) => {
      const v = prev[variantIdx];
      const nextDefaults = { ...v.defaults };
      if (value === '') {
        // Empty string typed = clear override (revert to inherit)
        // To explicitly set empty override, user must have a non-empty value first.
        // Per spec: key NOT in defaults = inherit.
        delete nextDefaults[key];
      } else {
        nextDefaults[key] = value;
      }
      const next = prev.map((vv, i) =>
        i === variantIdx ? { ...vv, defaults: nextDefaults } : vv
      );
      markDirty(fields, next);
      return next;
    });
  }

  // Toggle lock for a field in a variant
  // key must be a composite key: `${component_key}:${property_key}`
  function toggleVariantLock(variantIdx, key) {
    setVariants((prev) => {
      const v = prev[variantIdx];
      const current = v.locked_fields || [];
      const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      const nextVariants = prev.map((vv, i) =>
        i === variantIdx ? { ...vv, locked_fields: next } : vv
      );
      markDirty(fields, nextVariants);
      return nextVariants;
    });
  }

  // Bulk lock/unlock all non-RD-locked fields in a component group.
  // `componentFields` = the group.fields array (already filtered to one component).
  // `locked` = true → add all lockable composite keys; false → remove all lockable composite keys.
  function setComponentLock(variantIdx, componentFields, locked) {
    const lockableKeys = componentFields
      .filter((f) => !f.locked) // skip RD-locked fields
      .map((f) => `${f.component_key}:${f.property_key}`);
    setVariants((prev) => {
      const v = prev[variantIdx];
      const current = v.locked_fields || [];
      let next;
      if (locked) {
        // Union: add any key not already present
        const toAdd = lockableKeys.filter((k) => !current.includes(k));
        next = [...current, ...toAdd];
      } else {
        // Remove all lockable keys
        const toRemove = new Set(lockableKeys);
        next = current.filter((k) => !toRemove.has(k));
      }
      const nextVariants = prev.map((vv, i) =>
        i === variantIdx ? { ...vv, locked_fields: next } : vv
      );
      markDirty(fields, nextVariants);
      return nextVariants;
    });
  }

  // ── Validation ────────────────────────────────────────────────────────
  function validate() {
    const names = variants.map((v) => v.name.trim());
    for (const n of names) {
      if (!n) {
        addToast('每個 Variant 都必須填寫名稱');
        return false;
      }
    }
    if (new Set(names).size !== names.length) {
      addToast('Variant 名稱不可重複');
      return false;
    }
    const defaultCount = variants.filter((v) => v.is_default).length;
    if (defaultCount === 0) {
      addToast('必須指定一個預設 Variant');
      return false;
    }
    if (defaultCount > 1) {
      addToast('只能有一個預設 Variant（目前有 ' + defaultCount + ' 個）');
      return false;
    }
    return true;
  }

  function handleClose() {
    if (dirty && !window.confirm('尚未儲存，確定要離開嗎？')) return;
    onClose();
  }

  // ── Save ──────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const toSaveFields = fields
        .filter((f) => !f.locked)
        .map((f) => ({
          component_key: f.component_key,
          component_name: f.component_name,
          property_key: f.property_key,
          visible: f.visible,
          hidden_value: f.hidden_value || '',
          category: f.category === f.original_category ? '' : f.category,
          type_override: f.type_override || '',
          placeholder_size: f.placeholder_size || '',
        }));

      // AS-220: warn if all non-locked fields are hidden (clients will see no editable fields).
      const allHidden = toSaveFields.length > 0 && toSaveFields.every((f) => !f.visible);
      if (allHidden) {
        const confirmed = window.confirm(
          '此樣板所有欄位皆隱藏，客戶將無欄位可編輯，確定儲存？'
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
      }

      // variants.defaults only carries overridden keys (empty = inherit).
      // fields sends full structural config for every non-RD-locked field.
      const toSaveVariants = variants.map((v) => ({
        name: v.name.trim(),
        description: v.description || '',
        is_default: !!v.is_default,
        defaults: v.defaults || {},
        locked_fields: v.locked_fields || [],
        variant_key: v.variant_key || crypto.randomUUID(),
      }));

      await api.saveTemplateFieldConfig(template.uuid, toSaveFields, toSaveVariants);
      addToast('欄位設定已儲存', 'success');
      onClose();
    } catch (err) {
      addToast('儲存失敗：' + err.message);
    } finally {
      setSaving(false);
    }
  }

  const activeVariant = variants[activeVariantIdx];
  const isBaseVariant = activeVariant?.is_default;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        title={`欄位設定 — ${template.name}`}
        onClose={handleClose}
        size="2xl"
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={handleClose}>
              取消
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? '儲存中...' : '儲存設定'}
            </Button>
          </div>
        }
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spinner />
          </div>
        ) : (
          <div className="fce-layout">
            {/* ── Left sidebar: variant list ── */}
            <div className="fce-sidebar">
              <div className="fce-sidebar-label">Variants</div>
              <div className="fce-sidebar-list">
                {variants.map((v, i) => (
                  <VariantSidebarItem
                    key={i}
                    variant={v}
                    isActive={i === activeVariantIdx}
                    onClick={() => setActiveVariantIdx(i)}
                    onCopy={() => duplicateVariant(i)}
                    onDelete={!v.is_default ? () => deleteVariant(i) : undefined}
                    onRename={(newName) => {
                      const names = variants.map((vv, ii) => ii === i ? newName : vv.name);
                      if (new Set(names.map(n => n.trim())).size !== names.length) {
                        addToast('Variant 名稱不可重複');
                        return;
                      }
                      updateVariant(i, { name: newName });
                    }}
                  />
                ))}
              </div>
              {/* Add variant button with dropdown */}
              <div className="fce-add-wrap" ref={addDropdownRef}>
                <button
                  className="fce-add-btn"
                  onClick={() => setAddDropdownOpen((o) => !o)}
                >
                  <span className="fce-add-plus">+</span> 新增 Variant
                </button>
                {addDropdownOpen && (
                  <div className="fce-add-dropdown">
                    <div
                      className="fce-add-dropdown-item"
                      onClick={addBlankVariant}
                    >
                      從空白建立
                    </div>
                    <div className="fce-add-dropdown-section">從現有複製</div>
                    {variants.map((v, i) => (
                      <div
                        key={i}
                        className="fce-add-dropdown-item fce-add-dropdown-item-sub"
                        onClick={() => duplicateVariant(i)}
                      >
                        複製「{v.name || '（未命名）'}」
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right: field table ── */}
            <div className="fce-content">
              {/* Context banner for non-base variants */}
              {!isBaseVariant && (
                <div className="fce-context-banner">
                  <span>ℹ</span>
                  <span>
                    結構欄位（分類 / 型別 / 顯示）於<strong>「預設」</strong>
                    variant 設定，套用至所有 variant。此頁只有「預設值」與「鎖定」可操作。
                  </span>
                </div>
              )}

              {/* Info box */}
              <div className="fce-info-box">
                <span className="fce-info-tag">繼承</span> 預設值留空 = 沿用 meta_list / component / template 既有定義
                &nbsp;&nbsp;
                <span className="fce-info-tag">覆寫</span> 填入值 = 此 Variant 覆寫
                <div className="fce-info-rule">
                  <span className="fce-info-tag">鎖定</span> 鎖定 ON = Portal 不顯示此欄位，系統使用填入的固定值（客戶無法修改）
                </div>
              </div>

              {/* Field groups table */}
              <div className="fce-table-area">
                {fields.length === 0 ? (
                  <div className="fce-empty">
                    此 Template 沒有可設定的欄位（可能尚未快取 meta 資料，請先在 Portal 選擇此模板一次）
                  </div>
                ) : (
                  grouped.map((group, groupIdx) => (
                    <FieldGroup
                      key={`${group.componentKey}:${group.componentName}`}
                      group={group}
                      groupIdx={groupIdx}
                      isBaseVariant={isBaseVariant}
                      activeVariant={activeVariant}
                      activeVariantIdx={activeVariantIdx}
                      collapsed={!!collapsedGroups[groupIdx]}
                      onToggleCollapse={() =>
                        setCollapsedGroups((prev) => ({
                          ...prev,
                          [groupIdx]: !prev[groupIdx],
                        }))
                      }
                      getFieldIndex={getFieldIndex}
                      updateField={updateField}
                      setVariantDefault={setVariantDefault}
                      toggleVariantLock={toggleVariantLock}
                      setComponentLock={setComponentLock}
                    />
                  ))
                )}
              </div>

              {/* Legend */}
              <div className="fce-legend">
                <span className="fce-legend-title">圖例：</span>
                <span className="fce-legend-item">
                  <span className="fce-legend-swatch fce-legend-swatch-overridden" />
                  橘色左邊框 = 已覆寫
                </span>
                <span className="fce-legend-item">
                  <span className="fce-legend-swatch fce-legend-swatch-inherited" />
                  灰色 placeholder = 繼承
                </span>
                <span className="fce-legend-item">
                  <span className="fce-legend-swatch fce-legend-swatch-locked" />
                  隱藏且固定 = 鎖定 ON（Portal 不顯示）
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* New variant name modal */}
      {newVariantModal && (
        <NewVariantNameModal
          sourceVariantName={
            newVariantModal.sourceIdx !== null
              ? variants[newVariantModal.sourceIdx]?.name
              : null
          }
          existingNames={variants.map((v) => v.name)}
          onConfirm={confirmNewVariant}
          onCancel={() => setNewVariantModal(null)}
        />
      )}
    </>
  );
}

// ── VariantSidebarItem ────────────────────────────────────────────────────
function VariantSidebarItem({ variant, isActive, onClick, onCopy, onDelete, onRename }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const inputRef = useRef(null);

  function startEdit(e) {
    e.stopPropagation();
    setEditName(variant.name);
    setEditing(true);
  }

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  function commitEdit() {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== variant.name) {
      onRename(trimmed);
    }
    setEditing(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(false);
    e.stopPropagation();
  }

  const displayName = variant.name || '（未命名）';
  const labelWithSuffix = variant.is_default ? `${displayName}（預設）` : displayName;

  return (
    <div
      className={`fce-sidebar-item ${isActive ? 'fce-sidebar-item-active' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => !editing && e.key === 'Enter' && onClick()}
    >
      {variant.is_default && (
        <span className="fce-sidebar-base-badge">base</span>
      )}
      {editing ? (
        <input
          ref={inputRef}
          className="fce-sidebar-rename-input"
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="fce-sidebar-item-name" onDoubleClick={startEdit} title="雙擊改名">
          {labelWithSuffix}
        </span>
      )}
      <span className="fce-sidebar-actions">
        {!editing && (
          <button
            className="fce-sidebar-action-btn"
            title="改名此 Variant"
            onClick={startEdit}
          >
            改名
          </button>
        )}
        <button
          className="fce-sidebar-action-btn"
          title="複製此 Variant"
          onClick={(e) => {
            e.stopPropagation();
            onCopy();
          }}
        >
          複製
        </button>
        {onDelete && (
          <button
            className="fce-sidebar-action-btn fce-sidebar-action-delete"
            title="刪除此 Variant"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            刪除
          </button>
        )}
      </span>
    </div>
  );
}

// ── FieldGroup ────────────────────────────────────────────────────────────
function FieldGroup({
  group,
  groupIdx,
  isBaseVariant,
  activeVariant,
  activeVariantIdx,
  collapsed,
  onToggleCollapse,
  getFieldIndex,
  updateField,
  setVariantDefault,
  toggleVariantLock,
  setComponentLock,
}) {
  const overrideCount = useMemo(() => {
    if (!activeVariant) return 0;
    return group.fields.filter(
      (f) => `${f.component_key}:${f.property_key}` in (activeVariant.defaults || {})
    ).length;
  }, [group.fields, activeVariant]);

  // Compute lock stats for bulk control (skip RD-locked fields)
  const { lockableCount, lockedCount } = useMemo(() => {
    const lockable = group.fields.filter((f) => !f.locked);
    const locked = lockable.filter((f) =>
      (activeVariant?.locked_fields || []).includes(`${f.component_key}:${f.property_key}`)
    );
    return { lockableCount: lockable.length, lockedCount: locked.length };
  }, [group.fields, activeVariant]);

  // indeterminate: some but not all lockable fields are locked
  const allLocked = lockableCount > 0 && lockedCount === lockableCount;
  const someLocked = lockedCount > 0 && lockedCount < lockableCount;

  return (
    <div className="fce-group">
      <div className="fce-group-header" onClick={onToggleCollapse}>
        <span className={`fce-group-chevron ${collapsed ? 'fce-group-chevron-collapsed' : ''}`}>
          ▼
        </span>
        <span className="fce-group-comp-name">{group.componentName}</span>
        <span className="fce-group-comp-key">{group.componentKey}</span>
        {overrideCount > 0 && (
          <span className="fce-group-override-count">{overrideCount} 項已覆寫</span>
        )}
        <span className="fce-group-field-count">{group.fields.length} 個欄位</span>
        {lockableCount > 0 && (
          <span
            className="fce-group-bulk-lock"
            onClick={(e) => e.stopPropagation()}
          >
            <span className={`fce-group-lock-stat${someLocked ? ' fce-group-lock-stat--partial' : ''}`}>
              {lockedCount}/{lockableCount} 已鎖
            </span>
            <button
              className={`fce-group-bulk-btn${allLocked ? ' fce-group-bulk-btn--active' : ''}${someLocked ? ' fce-group-bulk-btn--indeterminate' : ''}`}
              title={allLocked ? '全部解鎖' : '全部鎖定'}
              onClick={(e) => {
                e.stopPropagation();
                setComponentLock(activeVariantIdx, group.fields, !allLocked);
              }}
            >
              {allLocked ? '全解鎖' : someLocked ? '全鎖定 ···' : '全鎖定'}
            </button>
          </span>
        )}
      </div>

      <table className="fce-table">
        <colgroup>
          <col className="fce-col-name" />
          <col className="fce-col-category" />
          <col className="fce-col-type" />
          <col className="fce-col-shown" />
          <col className="fce-col-lock" />
          <col className="fce-col-default" />
        </colgroup>
        <thead>
          <tr>
            <th>欄位名稱</th>
            <th>Category{isBaseVariant ? ' ✎' : ''}</th>
            <th>型別{isBaseVariant ? ' ✎' : ''}</th>
            <th>顯示{isBaseVariant ? ' ✎' : ''}</th>
            <th>鎖定（Portal 隱藏）</th>
            <th>預設值（空白 = 繼承；填入 = 覆寫）</th>
          </tr>
        </thead>
        <tbody>
          {collapsed ? (
            <tr>
              <td colSpan={6} className="fce-group-collapsed-msg">
                （群組已摺疊，共 {group.fields.length} 個欄位）
              </td>
            </tr>
          ) : (
            group.fields.map((field) => {
              const fieldIdx = getFieldIndex(field.component_key, field.property_key);
              const isLocked = field.locked; // RD-level lock
              const compositeKey = `${field.component_key}:${field.property_key}`;
              const variantLocked = (activeVariant?.locked_fields || []).includes(
                compositeKey
              );
              const overrideVal =
                activeVariant?.defaults &&
                compositeKey in activeVariant.defaults
                  ? activeVariant.defaults[compositeKey]
                  : undefined;
              const hasOverride = overrideVal !== undefined;

              return (
                <FieldTableRow
                  key={`${field.component_key}.${field.property_key}`}
                  field={field}
                  fieldIdx={fieldIdx}
                  isBaseVariant={isBaseVariant}
                  isRdLocked={isLocked}
                  variantLocked={variantLocked}
                  hasOverride={hasOverride}
                  overrideVal={overrideVal}
                  updateField={updateField}
                  onSetDefault={(val) =>
                    setVariantDefault(activeVariantIdx, compositeKey, val)
                  }
                  onToggleLock={() =>
                    toggleVariantLock(activeVariantIdx, compositeKey)
                  }
                />
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── FieldTableRow ─────────────────────────────────────────────────────────
function FieldTableRow({
  field,
  fieldIdx,
  isBaseVariant,
  isRdLocked,
  variantLocked,
  hasOverride,
  overrideVal,
  updateField,
  onSetDefault,
  onToggleLock,
}) {
  const inheritedDisplay = defaultValueStr(field.default_value);

  const rowClass = [
    'fce-row',
    hasOverride ? 'fce-row-overridden' : '',
    variantLocked ? 'fce-row-locked' : '',
    isRdLocked ? 'fce-row-rd-locked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (isRdLocked) {
    return (
      <tr className={rowClass}>
        <td className="fce-cell-name">
          <span className="fce-prop-key">{field.property_key}</span>
          <span className="fce-comp-label">{field.component_key}</span>
        </td>
        <td colSpan={5} className="fce-cell-rd-locked">
          <Icon name="Lock" size={13} />
          <span>已鎖定（RD 設定）</span>
          {field.locked_value && <code>{field.locked_value}</code>}
        </td>
      </tr>
    );
  }

  // Category cell
  const categoryCell = isBaseVariant ? (
    <select
      className="fce-select"
      value={field.category || field.original_category}
      onChange={(e) => updateField(fieldIdx, { category: e.target.value })}
    >
      {CATEGORIES.map((c) => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
    </select>
  ) : (
    <span className="fce-struct-readonly">
      <Badge variant="default">{field.category || field.original_category || '—'}</Badge>
    </span>
  );

  // Type cell
  const effectiveType = field.type_override || field.original_type;
  const typeCell = isBaseVariant && field.original_type === 'string' ? (
    <select
      className="fce-select"
      value={field.type_override || ''}
      onChange={(e) => updateField(fieldIdx, { type_override: e.target.value })}
    >
      {TYPE_OVERRIDES.map((t) => (
        <option key={t.value} value={t.value}>
          {t.label}
        </option>
      ))}
    </select>
  ) : (
    <span className="fce-type-tag">{effectiveType || field.original_type}</span>
  );

  // Shown/visible cell
  const shownCell = isBaseVariant ? (
    <select
      className="fce-select"
      value={field.visible ? '1' : '0'}
      onChange={(e) =>
        updateField(fieldIdx, {
          visible: e.target.value === '1',
          hidden_value:
            e.target.value === '1'
              ? ''
              : field.hidden_value || inheritedDisplay,
        })
      }
    >
      <option value="1">顯示</option>
      <option value="0">隱藏</option>
    </select>
  ) : (
    <span className="fce-struct-readonly fce-struct-readonly-text">
      {field.visible ? '顯示' : '隱藏'}
    </span>
  );

  // Lock cell
  const lockCell = (
    <div className="fce-lock-cell">
      <Toggle checked={variantLocked} onChange={onToggleLock} />
    </div>
  );

  // Default value cell
  const inputVal = hasOverride ? defaultValueStr(overrideVal) : '';
  const placeholderText = variantLocked
    ? hasOverride
      ? ''
      : `固定值（繼承：${inheritedDisplay || '未設定'}）`
    : `繼承：${
        inheritedDisplay
          ? inheritedDisplay.length > 32
            ? inheritedDisplay.substring(0, 32) + '…'
            : inheritedDisplay
          : '（未設定）'
      }`;

  const inputClass = [
    'fce-default-input',
    hasOverride ? 'fce-default-input-overridden' : '',
    variantLocked ? 'fce-default-input-locked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  let defaultValueCell;
  if (effectiveType === 'color') {
    const swatchColor = hasOverride ? overrideVal : inheritedDisplay;
    const isValidColor =
      swatchColor &&
      (swatchColor.startsWith('#') || swatchColor.startsWith('rgb'));
    defaultValueCell = (
      <div className="fce-default-cell">
        <div className="fce-color-wrap">
          <span
            className={`fce-color-swatch ${!hasOverride ? 'fce-color-swatch-inherited' : ''}`}
            style={isValidColor ? { background: swatchColor } : {}}
          />
          <input
            className={inputClass}
            type="text"
            value={inputVal}
            placeholder={placeholderText}
            onChange={(e) => onSetDefault(e.target.value)}
          />
        </div>
        {hasOverride && !variantLocked && (
          <Badge variant="primary" className="fce-badge-overridden">已覆寫</Badge>
        )}
      </div>
    );
  } else if (effectiveType === 'image' || effectiveType === 'medias') {
    const hint = effectiveType === 'image' ? '圖片 URL' : '媒體素材';
    defaultValueCell = (
      <div className="fce-default-cell">
        <div className="fce-input-wrap">
          <input
            className={inputClass}
            type="text"
            value={inputVal}
            placeholder={placeholderText}
            onChange={(e) => onSetDefault(e.target.value)}
          />
          <span className="fce-media-hint">{hint}</span>
        </div>
        {hasOverride && !variantLocked && (
          <Badge variant="primary" className="fce-badge-overridden">已覆寫</Badge>
        )}
      </div>
    );
  } else {
    defaultValueCell = (
      <div className="fce-default-cell">
        <div className="fce-input-wrap">
          <input
            className={inputClass}
            type="text"
            value={inputVal}
            placeholder={placeholderText}
            onChange={(e) => onSetDefault(e.target.value)}
          />
        </div>
        {hasOverride && !variantLocked && (
          <Badge variant="primary" className="fce-badge-overridden">已覆寫</Badge>
        )}
      </div>
    );
  }

  return (
    <tr className={rowClass}>
      <td className="fce-cell-name">
        <span className="fce-prop-key">{field.property_key}</span>
        <span className="fce-comp-label">{field.component_key}</span>
      </td>
      <td>{categoryCell}</td>
      <td>{typeCell}</td>
      <td>{shownCell}</td>
      <td>{lockCell}</td>
      <td className="fce-cell-default">{defaultValueCell}</td>
    </tr>
  );
}

// ── NewVariantNameModal ───────────────────────────────────────────────────
function NewVariantNameModal({ sourceVariantName, existingNames, onConfirm, onCancel }) {
  const [name, setName] = useState(
    sourceVariantName ? `${sourceVariantName} 複製` : ''
  );
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function handleConfirm() {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (existingNames.includes(trimmed)) {
      alert('此名稱已存在，請使用不同名稱');
      return;
    }
    onConfirm(trimmed);
  }

  return (
    <div className="fce-name-modal-overlay" onClick={onCancel}>
      <div
        className="fce-name-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="新增 Variant"
      >
        <div className="fce-name-modal-title">
          {sourceVariantName ? `從「${sourceVariantName}」複製` : '從空白建立 Variant'}
        </div>
        <label className="fce-name-modal-label">Variant 名稱</label>
        <input
          ref={inputRef}
          className="fce-name-modal-input"
          type="text"
          value={name}
          placeholder="輸入 Variant 名稱（必填）"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleConfirm();
            if (e.key === 'Escape') onCancel();
          }}
        />
        <div className="fce-name-modal-actions">
          <button className="fce-name-modal-cancel" onClick={onCancel}>
            取消
          </button>
          <button
            className="fce-name-modal-confirm"
            onClick={handleConfirm}
            disabled={!name.trim()}
          >
            建立
          </button>
        </div>
      </div>
    </div>
  );
}
